import { useMatch } from 'react-router-dom';
import { useState, useEffect } from 'react';

import { AppRoutes } from 'config';
import { useReaderStatusListQuery } from 'store/api/clover/reader-status';

// Cart reader status (plan P5): read when the cart opens, every 2 minutes
// while it stays mounted, and again when the iPad comes back to the tab.
// Display only: a failed read shows nothing and never raises a toast.
//
// Safety (coordinator, 21 Oct): a status read never runs while a sale may be
// starting. The Cart panel is reused on the checkout page, so this reads
// only on the products page itself (/products/:id, exact); never fresh=1
// (the list endpoint has no fresh); and polling pauses while the page is
// hidden, resuming when it is visible again.
export const READER_POLL_MS = 2 * 60 * 1000;
const REFOCUS_GAP_MS = 5000;

const isVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

export const useEventReaders = (eventId?: number, skip?: boolean) => {
  const onProductsPage = !!useMatch({
    end: true,
    path: AppRoutes.Products.Home,
  });
  const [visible, setVisible] = useState(isVisible);
  const off = !!skip || !eventId || !onProductsPage;

  const query = useReaderStatusListQuery(eventId ? { eventId } : undefined, {
    skip: off,
    refetchOnMountOrArgChange: true,
    pollingInterval: visible ? READER_POLL_MS : 0,
  });
  const { refetch, isUninitialized } = query;

  useEffect(() => {
    if (off) {
      return;
    }
    let lastAt = Date.now();
    const onChange = () => {
      const nowVisible = isVisible();
      setVisible(nowVisible);
      if (!nowVisible || isUninitialized) {
        return;
      }
      if (Date.now() - lastAt < REFOCUS_GAP_MS) {
        return;
      }
      lastAt = Date.now();
      try {
        refetch();
      } catch {
        // Display only.
      }
    };
    window.addEventListener('focus', onChange);
    document.addEventListener('visibilitychange', onChange);
    return () => {
      window.removeEventListener('focus', onChange);
      document.removeEventListener('visibilitychange', onChange);
    };
  }, [off, refetch, isUninitialized]);

  return off || query.isError ? undefined : query.data?.readers;
};
